import { useState, useMemo, useCallback, useEffect } from "react";
import { isBefore, isAfter, addMonths, subMonths, addYears, subYears, differenceInYears, min, setYear, max, setMonth } from "date-fns/fp";
function useMonthPicker({ initialValue, maxMonth, minMonth }) {
  const [yearInView, setYearInView] = useState(
    (initialValue || /* @__PURE__ */ new Date()).getFullYear()
  );
  const [highlightedDate, setHighlightedDate] = useState();
  const minYear = minMonth ? minMonth.getFullYear() : void 0;
  const maxYear = maxMonth ? maxMonth.getFullYear() : void 0;
  const defaultDate = useMemo(() => new Date(yearInView, 0, 1), [yearInView]);
  const changeHighlightedDate = useCallback(
    (changeFunc) => setHighlightedDate((date) => {
      if (!date) {
        return yearInView === minYear ? minMonth : defaultDate;
      }
      const newDate = changeFunc(date);
      return !isBefore(minMonth, newDate) && !isAfter(maxMonth, newDate) ? newDate : date;
    }),
    [defaultDate, maxMonth, minMonth, minYear, yearInView]
  );
  const clampHighlightedDate = useCallback(
    (changeFunc) => setHighlightedDate((date = defaultDate) => {
      const newDate = changeFunc(date);
      if (isBefore(minMonth, newDate)) {
        return minMonth;
      }
      if (isAfter(maxMonth, newDate)) {
        return maxMonth;
      }
      return newDate;
    }),
    [defaultDate, maxMonth, minMonth]
  );
  const viewNextYear = useCallback(() => {
    setHighlightedDate(void 0);
    setYearInView((year) => Math.min(year + 1, maxYear || Infinity));
  }, [maxYear]);
  const viewPreviousYear = useCallback(() => {
    setHighlightedDate(void 0);
    setYearInView((year) => Math.max(year - 1, minYear || -Infinity));
  }, [minYear]);
  const viewNextDecade = useCallback(() => {
    setHighlightedDate(void 0);
    setYearInView((year) => Math.min(year + 10, maxYear || Infinity));
  }, [maxYear]);
  const viewPreviousDecade = useCallback(() => {
    setHighlightedDate(void 0);
    setYearInView((year) => Math.max(year - 10, minYear || -Infinity));
  }, [minYear]);
  const highlightNextMonth = useCallback(() => {
    changeHighlightedDate(addMonths(1));
  }, [changeHighlightedDate]);
  const highlightPreviousMonth = useCallback(() => {
    changeHighlightedDate(subMonths(1));
  }, [changeHighlightedDate]);
  const highlightNextQuarter = useCallback(() => {
    changeHighlightedDate(addMonths(3));
  }, [changeHighlightedDate]);
  const highlightPreviousQuarter = useCallback(() => {
    changeHighlightedDate(subMonths(3));
  }, [changeHighlightedDate]);
  const highlightNextYear = useCallback(() => {
    if (yearInView !== maxYear) {
      clampHighlightedDate(addYears(1));
    }
  }, [clampHighlightedDate, maxYear, yearInView]);
  const highlightPreviousYear = useCallback(() => {
    if (yearInView !== minYear) {
      clampHighlightedDate(subYears(1));
    }
  }, [clampHighlightedDate, minYear, yearInView]);
  const highlightNextDecade = useCallback(() => {
    setHighlightedDate((date = defaultDate) => {
      if (!maxMonth || differenceInYears(date, maxMonth) > 10) {
        return addYears(10, date);
      }
      return min([setYear(maxYear, date), maxMonth]);
    });
  }, [defaultDate, maxMonth, maxYear]);
  const highlightPreviousDecade = useCallback(() => {
    setHighlightedDate((date = defaultDate) => {
      if (!minMonth || differenceInYears(minMonth, date) > 10) {
        return subYears(10, date);
      }
      return max([setYear(minYear, date), minMonth]);
    });
  }, [defaultDate, minMonth, minYear]);
  const highlightStartOfYear = useCallback(() => {
    clampHighlightedDate(setMonth(0));
  }, [clampHighlightedDate]);
  const highlightEndOfYear = useCallback(() => {
    clampHighlightedDate(setMonth(11));
  }, [clampHighlightedDate]);
  useEffect(() => {
    if (highlightedDate) {
      setYearInView(highlightedDate.getFullYear());
    }
  }, [highlightedDate]);
  return {
    highlightEndOfYear,
    highlightNextDecade,
    highlightNextMonth,
    highlightNextQuarter,
    highlightNextYear,
    highlightPreviousDecade,
    highlightPreviousMonth,
    highlightPreviousQuarter,
    highlightPreviousYear,
    highlightStartOfYear,
    highlightedDate,
    setHighlightedDate,
    viewNextDecade,
    viewNextYear,
    viewPreviousDecade,
    viewPreviousYear,
    yearInView
  };
}
export {
  useMonthPicker
};
